/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020, 2021
 *
 *==================================================
 */
//Standard libraries
import React, { lazy, useMemo } from "react";
import { Navigate, useLocation } from "react-router";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import Axios, { Canceler } from "axios";
import getDisplayName from "react-display-name";
//Foundation libraries
import { useSite } from "../../_foundation/hooks/useSite";
import { HOME } from "../constants/common";
//Redux
import { GET_SEO_CONFIG_ACTION } from "../../redux/actions/seo";
import { seoSelector } from "../../redux/selectors/seo";
//UI
// import { StyledProgressPlaceholder } from "@hcl-commerce-store-sdk/react-component";
//UA
//import UADataService from "../gtm/ua/uaData.service";
//GA4

import { PRODUCT_TOKEN } from "../constants/common";
import { withProductContext } from "../context/product-context";
import UADataService from "../gtm/data.service";
//import GA4DataService from "../gtm/ga4/ga4Data.service";
import { CONTENT_GROUP } from "../constants/gtm";
import PageLoader from "../../components/elements/loader/PageLoader";

const layoutComponentMap: Record<string, React.ComponentType<any>> = {
  "b2b-product-page": lazy(() => import("../../components/commerce-layouts/b2b-product-page")),
  "bundle-page": lazy(() => import("../../components/commerce-layouts/bundle-page")),
  "cart-page": lazy(() => import("../../components/commerce-layouts/cart-page")),
  "category-landing-page": lazy(() => import("../../components/commerce-layouts/category-landing-page")),
  "home-page": lazy(() => import("../../components/commerce-layouts/home-page")),
  "kit-page": lazy(() => import("../../components/commerce-layouts/kit-page")),
  "kitchen-model-page": lazy(() => import("../../components/commerce-layouts/kitchen-model-page")),
  "not-found": lazy(() => import("../../components/commerce-layouts/not-found")),
  "order-confirmation-page": lazy(() => import("../../components/commerce-layouts/order-confirmation-page")),
  "product-listing-page": lazy(() => import("../../components/commerce-layouts/product-listing-page")),
  "product-page": lazy(() => import("../../components/commerce-layouts/product-page")),
};

const SEO: React.FC = (props: any) => {
  const widgetName = getDisplayName(SEO);
  const { mySite: site } = useSite();
  const dispatch = useDispatch();
  const seoConfig = useSelector(seoSelector);
  const location = useLocation();
  const { pathname } = location;
  const identifier = useMemo(() => {
    let splitPath: string[];
    if (pathname.endsWith("/")) {
      const newPathname = pathname.slice(0, -1);
      window.history.replaceState(null, "", newPathname);
      splitPath = newPathname.split("/");
    } else {
      splitPath = pathname.split("/");
    }
    const _i = splitPath[splitPath.length - 1] || HOME;
    //const tabUri =_i.split(CHILD_ROUTE_SEPARATOR);
    //Rest client is using URLSearchParams to encode the query parameters.
    return decodeURI(_i);
    //return decodeURI(tabURI[tabURI.length - 1]);
  }, [pathname]);
  const [first, setFirst] = React.useState(true);

  const cancels: Canceler[] = [];
  const CancelToken = Axios.CancelToken;
  const payloadBase: any = {
    widget: widgetName,
    cancelToken: new CancelToken(function executor(c) {
      cancels.push(c);
    }),
  };

  React.useEffect(() => {
    if (site !== null && identifier) {
      dispatch(
        GET_SEO_CONFIG_ACTION({
          identifier,
          ...payloadBase,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, identifier]);

  React.useEffect(
    () => () => {
      cancels.forEach((cancel) => cancel());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  React.useEffect(() => {
    if (first && identifier && seoConfig && seoConfig[identifier]) {
      setFirst(false);
    }
  }, [first, identifier, seoConfig]);

  const ActiveC = useMemo(() => {
    if (seoConfig && seoConfig[identifier]) {
      const c = seoConfig[identifier];
      const token = c.tokenName;
      const Component = layoutComponentMap[c.layout.containerName];
      return token === PRODUCT_TOKEN ? withProductContext(Component) : Component;
    } else {
      return () => <></>;
    }
  }, [seoConfig, identifier]);

  const ActiveComponent = useMemo(
    () => {
      if (seoConfig && seoConfig[identifier]) {
        const c = seoConfig[identifier];
        const slots = c.layout.slots;

        //GA360
        if (site.enableGA && seoConfig[identifier].page) {
          if (site.enableUA) {
            UADataService.setPageData(seoConfig[identifier].page.type, CONTENT_GROUP.CONTENT);
          }
          // if (site.enableGA4) {
          //   GA4DataService.setPageTitle(seoConfig[identifier].page.title);
          // }
        }
        return () =>
          c.redirect && c.redirect.trim() !== "" ? (
            <>
              <Helmet>
                <meta name="prerender-status-code" content="301" />
              </Helmet>
              <Navigate replace to={`/produits/${c.redirect}`} />
            </>
          ) : (
            <div className="page">
              <Helmet>
                <meta charSet="utf-8" />
                <meta name="description" content={c.page.metaDescription}></meta>
                <meta name="keywords" content={c.page.metaKeyword}></meta>
                <title>{c?.layout?.name?.trim() === "Product List Page Layout" ? "" : c.page.title}</title>
              </Helmet>
              <ActiveC page={{ ...c.page }} slots={slots} {...props} />
            </div>
          );
      } else {
        return () => <PageLoader minHeight="100vh" />;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seoConfig, identifier, site, ActiveC]
  );

  return <>{!first ? <ActiveComponent /> : <PageLoader minHeight="100vh" />}</>;
};

export default SEO;
